import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Flame, Home, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleProvider";
import { useKotBoardLive } from "@/hooks/useKotBoardLive";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { displayItemName } from "@/lib/menuLocale";
import "@/styles/simple-ui.css";

const FILTERS = ["All", "New", "Preparing", "Ready"];

const STATUS_BG = {
  New: "bg-blue-600",
  Preparing: "bg-amber-500",
  Ready: "bg-emerald-600",
  Served: "bg-slate-400",
};

const ADVANCE = {
  New: { labelKey: "simple.kdsStart", fallback: "Start", icon: Flame, btn: "bg-amber-500" },
  Preparing: { labelKey: "simple.kdsReady", fallback: "Ready", icon: CheckCircle2, btn: "bg-emerald-600" },
  Ready: { labelKey: "simple.kdsServed", fallback: "Served", icon: CheckCircle2, btn: "bg-slate-800" },
};

function normalizeStatus(status) {
  return status === "Completed" ? "Served" : status;
}

export default function SimpleKitchen() {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const branchId = user?.default_branch?.id;
  const [filter, setFilter] = useState("All");
  useKotBoardLive(branchId);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["kot-tickets", branchId, "all", filter],
    queryFn: async () => {
      const params = { branch_id: branchId, status: filter };
      const { data: res } = await api.get("/kot/tickets", { params });
      return res;
    },
    enabled: Boolean(branchId),
    refetchInterval: 8000,
  });

  const orders = useMemo(() => {
    const list = data?.orders || data?.tickets || [];
    return list.filter((o) => normalizeStatus(o.status) !== "Served");
  }, [data]);

  const advanceMutation = useMutation({
    mutationFn: async (id) => {
      const { data: res } = await api.post(
        `/kot/tickets/${id}/advance`,
        {},
        { params: { branch_id: branchId } },
      );
      return res;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["kot-tickets", branchId] });
      toast.success(`KOT → ${res.order?.status || "updated"}`);
    },
    onError: (err) => {
      toast.error(err.response?.data?.errors?.status?.[0] || err.response?.data?.message || "Failed");
    },
  });

  if (!branchId) {
    return (
      <div className="grid h-full place-items-center p-6 text-slate-500">
        {t("pos.selectBranchCounter", "Select a branch first")}
      </div>
    );
  }

  return (
    <div className="simple-ui flex h-full flex-col" dir={isRtl ? "rtl" : "ltr"} data-testid="simple-kitchen">
      <div className="flex flex-wrap items-center gap-2 border-b bg-white px-3 py-2">
        <Link to="/" className="inline-flex h-10 items-center gap-1 rounded-xl bg-violet-50 px-3 text-sm font-bold text-violet-700">
          <Home className="h-4 w-4" />
          {t("common.home")}
        </Link>
        <h1 className="flex-1 text-lg font-extrabold text-slate-900">{t("nav.kitchenOrders")}</h1>
        <button type="button" onClick={() => refetch()} className="grid h-10 w-10 place-items-center rounded-xl bg-slate-100" disabled={isFetching}>
          <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto border-b bg-white px-3 py-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={cn(
              "shrink-0 rounded-full px-4 py-2 text-sm font-bold",
              filter === f ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600",
            )}
          >
            {f === "All" ? t("simple.posAll", "All") : f}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {isLoading ? (
          <div className="grid place-items-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : orders.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400">{t("simple.kdsEmpty", "No kitchen tickets")}</div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {orders.map((order) => {
              const status = normalizeStatus(order.status);
              const advance = ADVANCE[status];
              const AdvanceIcon = advance?.icon;
              const lines = order.lines || order.items || [];
              return (
                <div
                  key={order.id}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
                  data-testid={`simple-kot-${order.id}`}
                >
                  <div className={cn("flex items-center justify-between px-4 py-3 text-white", STATUS_BG[status] || STATUS_BG.New)}>
                    <div>
                      <div className="text-lg font-black">
                        {order.table_code || order.table || order.order_type || "—"}
                      </div>
                      <div className="text-xs font-semibold opacity-90">
                        {order.ticket_no || order.kot_number || order.bill_number || `#${order.id}`}
                      </div>
                    </div>
                    <span className="rounded-full bg-white/20 px-3 py-1 text-sm font-extrabold">{status}</span>
                  </div>
                  <ul className="space-y-2 p-4">
                    {lines.map((line, idx) => (
                      <li key={line.id || idx} className="flex items-center justify-between gap-2">
                        <span className="truncate text-[15px] font-bold text-slate-900">
                          {displayItemName(line) || line.name || line.item_name}
                        </span>
                        <span className="shrink-0 font-mono text-base font-black text-slate-700">×{line.qty}</span>
                      </li>
                    ))}
                  </ul>
                  {advance ? (
                    <div className="border-t p-3">
                      <button
                        type="button"
                        disabled={advanceMutation.isPending}
                        onClick={() => advanceMutation.mutate(order.id)}
                        className={cn(
                          "flex h-14 w-full items-center justify-center gap-2 rounded-xl text-lg font-extrabold text-white disabled:opacity-50",
                          advance.btn,
                        )}
                      >
                        {AdvanceIcon ? <AdvanceIcon className="h-5 w-5" /> : null}
                        {t(advance.labelKey, advance.fallback)}
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
